import React, { useState, useMemo } from 'react';
import {
  Layout, Menu, Tabs, Row, Col, Typography, Button, Space, Input, Badge,
  Avatar, Card, Progress, Alert, Upload, Dropdown, Form, Collapse,
  InputNumber, message
} from 'antd';
import {
  HomeOutlined, BarChartOutlined, FileTextOutlined, TrophyOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, InfoCircleOutlined,
  BellOutlined, InboxOutlined, LogoutOutlined, SearchOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Dragger } = Upload;
const { Panel } = Collapse;

const YEARS = [2025, 2024, 2023, 2022];

const rangeScore = (value, ranges) => {
  for (const r of ranges) if (value <= r.max) return r.score;
  return ranges[ranges.length - 1].score;
};
const medalScore = n => (n >= 3 ? 1.5 : n === 2 ? 1 : n === 1 ? 0.5 : 0);

const SCORECARD = [
  {
    category: 'Студенты и их достижения',
    indicators: [
      {
        id: 'stu_prize_int',
        label: 'WorldSkills / конкурсы – МЕЖДУНАРОДНЫЙ уровень (посл. 3 года)',
        fields: ['1 место', '2 место', '3 место', 'Медальоны'],
        compute: ({ f0 = 0, f1 = 0, f2 = 0, f3 = 0 }) => {
          const first  = f0 === 0 ? 0 : f0 === 1 ? 3 : f0 === 2 ? 6 : 9;
          const second = f1 === 0 ? 0 : f1 === 1 ? 2 : f1 === 2 ? 4 : 6;
          const third  = f2 === 0 ? 0 : f2 === 1 ? 1 : f2 === 2 ? 2 : 3;
          return first + second + third + medalScore(f3);
        },
      },
      {
        id: 'stu_prize_nat',
        label: 'WorldSkills / конкурсы – РЕСПУБЛИКАНСКИЙ уровень (посл. 3 года)',
        fields: ['1 место', '2 место', '3 место'],
        compute: ({ f0 = 0, f1 = 0, f2 = 0 }) => {
          const first  = f0 === 0 ? 0 : f0 === 1 ? 2 : f0 === 2 ? 4 : 6;
          const second = f1 === 0 ? 0 : f1 === 1 ? 1 : f1 === 2 ? 2 : f1 === 3 ? 3 : 4;
          const third  = f2 === 0 ? 0 : f2 === 1 ? 0.5 : f2 === 2 ? 1 : 1.5;
          return first + second + third;
        },
      },
      {
        id: 'stu_demo_exam',
        label: 'Доля студентов, сдавших демоэкзамен WorldSkills с Skills Passport, %',
        percent: true,
        compute: v => rangeScore(v, [
          { max: 30, score: 1 },
          { max: 50, score: 2 },
          { max: 100, score: 3 },
        ]),
      },
    ],
  },
  {
    category: 'Кадровый состав',
    indicators: [
      {
        id: 'teach_int_cert',
        label: 'Доля педагогов с международными сертификатами, %',
        percent: true,
        compute: v => rangeScore(v, [
          { max: 4, score: 1 },
          { max: 9, score: 2 },
          { max: 100, score: 3 },
        ]),
      },
      {
        id: 'teach_research',
        label: 'Доля педагогов-исследователей / экспертов, %',
        percent: true,
        compute: v => rangeScore(v, [
          { max: 10, score: 1 },
          { max: 20, score: 2 },
          { max: 30, score: 3 },
        ]),
      },
      {
        id: 'teach_industry',
        label: 'Доля специалистов с производственным опытом ≥3 лет, %',
        percent: true,
        compute: v => rangeScore(v, [
          { max: 2, score: 1 },
          { max: 4, score: 2 },
          { max: 100, score: 3 },
        ]),
      },
      {
        id: 'teach_prize_int',
        label: 'Призовые места педагогов на МЕЖДУНАРОДНЫХ конкурсах (посл. 3 года)',
        fields: ['1 место', '2 место', '3 место', 'Медальоны'],
        compute: ({ f0 = 0, f1 = 0, f2 = 0, f3 = 0 }) => {
          const first  = f0 === 0 ? 0 : f0 === 1 ? 3 : f0 === 2 ? 6 : 9;
          const second = f1 === 0 ? 0 : f1 === 1 ? 2 : f1 === 2 ? 4 : 6;
          const third  = f2 === 0 ? 0 : f2 === 1 ? 1 : f2 === 2 ? 2 : 3;
          return first + second + third + medalScore(f3);
        },
      },
      {
        id: 'teach_prize_nat',
        label: 'Призовые места педагогов на РЕСПУБЛИКАНСКИХ конкурсах (посл. 3 года)',
        fields: ['1 место', '2 место', '3 место'],
        compute: ({ f0 = 0, f1 = 0, f2 = 0 }) => {
          const first  = f0 === 0 ? 0 : f0 === 1 ? 2 : f0 === 2 ? 4 : 6;
          const second = f1 === 0 ? 0 : f1 === 1 ? 1 : f1 === 2 ? 2 : f1 === 3 ? 3 : 4;
          const third  = f2 === 0 ? 0 : f2 === 1 ? 0.5 : f2 === 2 ? 1 : 1.5;
          return first + second + third;
        },
      },
    ],
  },
  {
    category: 'Международное сотрудничество',
    indicators: [
      {
        id: 'stu_mobility',
        label: 'Студенты – академическая мобильность (чел.)',
        count: true,
        compute: n => Math.min(n * 0.5, 10),
      },
      {
        id: 'teach_mobility',
        label: 'Педагоги – академическая мобильность (чел.)',
        count: true,
        compute: n => Math.min(n * 0.5, 10),
      },
    ],
  },
  {
    category: 'Соответствие ОП международным требованиям',
    indicators: [
      {
        id: 'prog_int_count',
        label: 'Количество образовательных программ, прошедших аккредитацию',
        count: true,
        compute: n => (n >= 3 ? 6 : n === 2 ? 4 : n === 1 ? 2 : 0),
      },
      {
        id: 'stu_prog_percent',
        label: 'Доля студентов, обучающихся по аккредитованным программам, %',
        percent: true,
        compute: v => rangeScore(v, [
          { max: 5, score: 1 },
          { max: 10, score: 2 },
          { max: 49, score: 3 },
          { max: 100, score: 4 },
        ]),
      },
    ],
  },
  {
    category: 'Карьерные перспективы выпускников',
    indicators: [
      {
        id: 'stu_cert_percent',
        label: 'Доля студентов, получивших сертификаты, %',
        percent: true,
        compute: v => rangeScore(v, [
          { max: 49, score: 1 },
          { max: 79, score: 2 },
          { max: 100, score: 3 },
        ]),
      },
      {
        id: 'employment_percent',
        label: 'Доля выпускников, трудоустроенных по специальности в первый год, %',
        percent: true,
        compute: v => rangeScore(v, [
          { max: 59, score: 1 },
          { max: 79, score: 2 },
          { max: 100, score: 3 },
        ]),
      },
    ],
  },
  {
    category: 'Предпринимательские инициативы и стартапы',
    indicators: [
      {
        id: 'startup_center',
        label: 'Наличие бизнес-стартап центра',
        boolean: true,
        compute: v => (v ? 3 : 0),
      },
      {
        id: 'startup_count',
        label: 'Количество стартап-проектов, вышедших на рынок (посл. 3 года)',
        count: true,
        compute: n => (n >= 3 ? 4 : n === 2 ? 3 : n === 1 ? 2 : 0),
      },
    ],
  },
];


const CURRENT_SURVEY = {
  title: 'Рейтинг колледжей 2025',
  deadline: '30 августа 2025',
  answers: 12,
  expected: 30,
};

const uploadProps = { beforeUpload: () => false, multiple: false, showUploadList: false };

export default function SurveysDashboard() {
  const [collapsed, setCollapsed] = useState(false);
  const [year, setYear]           = useState(2025);
  const [search, setSearch]       = useState('');
  const [bsForm]                  = Form.useForm();
  const [bsScores, setBsScores]   = useState({});

  const handleBsChange = (_, all) => {
    const newScores = {};
    SCORECARD.forEach(cat => cat.indicators.forEach(ind => {
      newScores[ind.id] = ind.compute(all[ind.id] || 0);
    }));
    setBsScores(newScores);
  };
  const bsTotal = useMemo(() => Object.values(bsScores).reduce((s, v) => s + v, 0), [bsScores]);

  
  const profileMenu = {
    items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'Выйти' }],
  };

 
  const handleSave = async () => {
    try {
      await bsForm.validateFields();
      console.log('Баллы:', bsScores);
      console.log('Файлы:', bsForm.getFieldsValue());
      message.success('Сохранено локально');
    } catch {
      message.error('Пожалуйста, заполните все поля');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        width={220}
        theme="light"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}
      >
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            paddingInline: 24,
          }}
        >
          {collapsed ? (
             <svg width="25" height="51" viewBox="0 0 45 51" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.77487 16.6822L0 12.1542V39.3224L7.77487 43.3738V34.3177L22.8534 42.4206V33.3645L7.77487 25.2617V16.6822Z" fill="#113F71"/>
<path d="M15.0785 20.257L8.48168 16.2056L22.8534 8.1028L15.7853 4.0514L22.8534 0L33.9267 6.0771L45 12.1542L37.9319 16.2056L30.1571 12.1542L15.0785 20.257Z" fill="#113F71"/>
<path d="M37.4607 16.6822L30.1571 20.257V38.8458L23.3246 42.4206V51L45 39.3224V34.9135V30.5047L41.466 32.4112L37.9319 34.3177L37.4607 16.6822Z" fill="#113F71"/>
</svg>
          ) : (
           <svg width="143" height="51" viewBox="0 0 223 51" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.77487 16.6822L0 12.1542V39.3224L7.77487 43.3738V34.3177L22.8534 42.4206V33.3645L7.77487 25.2617V16.6822Z" fill="#113F71"/>
<path d="M15.0785 20.257L8.48168 16.2056L22.8534 8.1028L15.7853 4.0514L22.8534 0L33.9267 6.0771L45 12.1542L37.9319 16.2056L30.1571 12.1542L15.0785 20.257Z" fill="#113F71"/>
<path d="M37.4607 16.6822L30.1571 20.257V38.8458L23.3246 42.4206V51L45 39.3224V34.9135V30.5047L41.466 32.4112L37.9319 34.3177L37.4607 16.6822Z" fill="#113F71"/>
<path d="M77.4734 43H72.0291V12.9583H62V8H87.5025V12.9583H77.4734V43Z" fill="#113F71"/>
<path d="M104.695 8L90.3679 43H96.9585L106.701 18.5L116.157 43H121.888L108.134 8H104.695Z" fill="#113F71"/>
<path d="M152.548 43H130.484V8H135.929V38.3333H152.548V43Z" fill="#113F71"/>
<path d="M173.466 8L159.425 43H165.443L175.185 18.5L185.214 43H190.945L177.191 8H173.466Z" fill="#113F71"/>
<path d="M196.963 8V43H202.407V34.25H211.576C213.869 34.25 215.535 33.571 217.021 32.5011C228.89 23.9525 220.97 8 211.576 8H196.963Z" fill="#113F71"/>
<path d="M202 29V14H210.91C220.808 16.2233 217.035 29 210.91 29H202Z" fill="white"/>
</svg>
          )}
        </div>

        <Menu
          mode="inline"
          defaultSelectedKeys={['dashboard']}
          items={[
            { key: 'dashboard', icon: <HomeOutlined />, label: <Link to="/dashboard">Кабинет</Link> },
            { key: 'opros',     icon: <FileTextOutlined />, label: <Link to="/opros">Опросы</Link> },
            { key: 'manager',   icon: <TrophyOutlined />,   label: <Link to="/manager">Рейтинги</Link> },
            { key: 'profile',   icon: <BarChartOutlined />, label: <Link to="/profile">Профиль</Link> },
          ]}
        />
      </Sider>

      <Layout>
      
        <Header style={{
          background: '#fff', paddingInline: 24,
          borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', gap: 16
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />

          <Title level={4} style={{ margin: 0, flexShrink: 0 }}>Опросы колледжей</Title>

          <Input
            prefix={<SearchOutlined />}
            placeholder="Поиск вопросов..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ maxWidth: 240, marginLeft: 'auto' }}
          />

          <Space size="large">
            <Badge count={3}><BellOutlined style={{ fontSize: 18 }} /></Badge>
            <Dropdown menu={profileMenu} placement="bottomRight">
              <Avatar style={{ background: '#4B32C3', cursor: 'pointer' }}>AK</Avatar>
            </Dropdown>
          </Space>
        </Header>

        
        <Content style={{ background: '#F5F7FB', padding: '32px 48px' }}>
          <Row justify="space-between" align="middle" gutter={16} style={{ marginBottom: 24 }}>
            <Col>
              <Title level={3} style={{ margin: 0 }}>Ежегодные опросы</Title>
              <Text type="secondary">Результаты опросов по качеству образования</Text>
            </Col>
          </Row>

       
          <Tabs
            activeKey={String(year)}
            onChange={k => setYear(Number(k))}
            items={YEARS.map(y => ({ key: String(y), label: y }))}
            style={{ background: '#fff', paddingInline: 16, borderRadius: 8, marginBottom: 24 }}
          />

          {year !== 2025 ? (
            <Card bordered={false} style={{ textAlign: 'center', padding: 64 }}>
              <Title level={4}>Данные за {year} год ещё не загружены</Title>
              <Text type="secondary">Выберите другой год или загрузите результаты.</Text>
            </Card>
          ) : (
            <>
            
              <Alert
                type="info" showIcon icon={<InfoCircleOutlined />}
                message="Текущий опрос активен"
                description={<>
                  <div style={{ marginBottom: 16 }}>
                    «{CURRENT_SURVEY.title}» – до {CURRENT_SURVEY.deadline}.<br />
                    Получено {CURRENT_SURVEY.answers.toLocaleString('ru-RU')} ответов из&nbsp;
                    {CURRENT_SURVEY.expected.toLocaleString('ru-RU')}.
                  </div>
                  <Progress
                    percent={Math.round(CURRENT_SURVEY.answers / CURRENT_SURVEY.expected * 100)}
                    showInfo={false} strokeColor="#4B32C3"
                  />
                  <Text type="secondary">
                    {Math.round(CURRENT_SURVEY.answers / CURRENT_SURVEY.expected * 100)} % завершено
                  </Text>
                </>}
                style={{ background: '#F0F5FF', borderRadius: 8, padding: 24, marginBottom: 32 }}
              />

         
              <Card title="Сбалансированная система показателей (ТиПО)" bordered={false}>
                <Form layout="vertical" form={bsForm} onValuesChange={handleBsChange}>
                  <Collapse bordered={false} defaultActiveKey={SCORECARD.map((_,i)=>i.toString())}>
                    {SCORECARD.map((cat,i)=>(
                      <Panel key={i} header={<b>{cat.category}</b>}>
                        {cat.indicators.map(ind=>(
                          <Card key={ind.id} size="small" style={{ marginBottom:16 }}>
                            <Row gutter={24} align="middle">
                              <Col flex="auto">
                                <Form.Item label={ind.label} name={ind.id}>
                                  {ind.percent && <InputNumber min={0} max={100} addonAfter="%" style={{ width:'100%' }} />}
                                  {ind.count   && <InputNumber min={0} style={{ width:'100%' }} />}
                                  {ind.fields  && <Row gutter={8}>
                                    {ind.fields.map((f,idx)=>(
                                      <Col span={6} key={idx}>
                                        <Form.Item name={[ind.id,`f${idx}`]} noStyle>
                                          <InputNumber min={0} placeholder={f} style={{ width:'100%' }} />
                                        </Form.Item>
                                      </Col>
                                    ))}
                                  </Row>}
                                </Form.Item>
                                <Form.Item
                                  label="Подтверждающие документы"
                                  name={[ind.id,'file']}
                                  valuePropName="fileList"
                                  getValueFromEvent={e=>e&&e.fileList}
                                >
                                  <Dragger {...uploadProps}>
                                    <p><InboxOutlined /></p>
                                    <p>Перетащите PDF или нажмите</p>
                                  </Dragger>
                                </Form.Item>
                              </Col>
                              <Col flex="120px" style={{ textAlign:'center' }}>
                                <Text>Баллы</Text>
                                <Title level={3}>{bsScores[ind.id]||0}</Title>
                              </Col>
                            </Row>
                          </Card>
                        ))}
                      </Panel>
                    ))}
                  </Collapse>

                  <Space direction="vertical" style={{ width:'100%', marginTop:24 }}>
                    <Progress percent={Math.round(bsTotal)} strokeColor="#4B32C3" />
                    <Button type="primary" onClick={handleSave}>Сохранить</Button>
                  </Space>
                </Form>
              </Card>
            </>
          )}
        </Content>
      </Layout>
    </Layout>
  );
}
